/* ============================================
   Upsell variant picker
   Handles ".js-upsell-variant-trigger" clicks
   (cross-sell cards + sticky upsell) for any
   product that has more than one real variant.
   Never auto-selects a size — the shopper must
   pick a value for every option before the
   "Add to cart" button is enabled. Sold-out
   values are shown struck-through and disabled.
   Does not modify assets/theme.js in any way;
   it only calls the public theme.miniCart API
   once an item has actually been added.
   ============================================ */
(function () {
  var overlay, sheet, imageEl, titleEl, priceEl, optionsEl, addBtn, closeBtn, errorEl;
  var currentProduct = null;
  var selectedOptions = [];

  function normalizeOptionValue(v) {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') return String(v.value || v.name || '').trim();
    return String(v).trim();
  }

  function getOptionDisplayName(optionName) {
    if (optionName && typeof optionName === 'object') {
      return optionName.name || '';
    }
    return optionName || '';
  }

  function buildModal() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'upsell-variant-overlay';
    overlay.innerHTML =
      '<div class="upsell-variant-sheet" role="dialog" aria-modal="true" aria-label="Choose options">' +
        '<button type="button" class="upsell-variant-sheet__close" aria-label="Close">&times;</button>' +
        '<div class="upsell-variant-sheet__product">' +
          '<img class="upsell-variant-sheet__image" alt="">' +
          '<div>' +
            '<div class="upsell-variant-sheet__title"></div>' +
            '<div class="upsell-variant-sheet__price"></div>' +
          '</div>' +
        '</div>' +
        '<div class="upsell-variant-sheet__options"></div>' +
        '<div class="upsell-variant-sheet__error" hidden></div>' +
        '<button type="button" class="upsell-variant-sheet__add" disabled>Select options</button>' +
      '</div>';
    document.body.appendChild(overlay);

    sheet = overlay.querySelector('.upsell-variant-sheet');
    imageEl = overlay.querySelector('.upsell-variant-sheet__image');
    titleEl = overlay.querySelector('.upsell-variant-sheet__title');
    priceEl = overlay.querySelector('.upsell-variant-sheet__price');
    optionsEl = overlay.querySelector('.upsell-variant-sheet__options');
    addBtn = overlay.querySelector('.upsell-variant-sheet__add');
    closeBtn = overlay.querySelector('.upsell-variant-sheet__close');
    errorEl = overlay.querySelector('.upsell-variant-sheet__error');

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
    });
    addBtn.addEventListener('click', handleAdd);
  }

  function openModal(handle) {
    buildModal();
    overlay.classList.add('is-open');
    document.body.classList.add('upsell-variant-open');
    titleEl.textContent = '';
    priceEl.textContent = '';
    optionsEl.innerHTML = '';
    errorEl.hidden = true;
    addBtn.disabled = true;
    addBtn.textContent = 'Loading\u2026';

    fetch('/products/' + handle + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(function (product) {
        currentProduct = product;
        selectedOptions = new Array(product.options.length).fill(null);
        renderProduct(product);
      })
      .catch(function () {
        optionsEl.innerHTML = '<p class="upsell-variant-sheet__error-text">Sorry, something went wrong loading this product.</p>';
        addBtn.textContent = 'Select options';
      });
  }

  function closeModal() {
    overlay.classList.remove('is-open');
    document.body.classList.remove('upsell-variant-open');
  }

  function renderProduct(product) {
    imageEl.src = product.featured_image ? (product.featured_image + '&width=160') : '';
    imageEl.alt = product.title;
    titleEl.textContent = product.title;
    priceEl.textContent = formatMoney(product.price);

    optionsEl.innerHTML = '';
    product.options.forEach(function (optionName, index) {
      var values = uniqueValuesForOption(product, index);
      var group = document.createElement('div');
      group.className = 'upsell-variant-sheet__group';

      var label = document.createElement('div');
      label.className = 'upsell-variant-sheet__label';
      label.textContent = getOptionDisplayName(optionName);
      group.appendChild(label);

      var row = document.createElement('div');
      row.className = 'upsell-variant-sheet__values';
      values.forEach(function (value) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'upsell-variant-sheet__value';
        btn.textContent = value;

        var available = isValueAvailable(product, index, value);
        if (!available) {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.addEventListener('click', function () {
            selectedOptions[index] = value;
            Array.prototype.forEach.call(row.children, function (child) {
              child.classList.remove('is-selected');
            });
            btn.classList.add('is-selected');
            updateAddState(product);
          });
        }
        row.appendChild(btn);
      });
      group.appendChild(row);
      optionsEl.appendChild(group);
    });

    updateAddState(product);
  }

  function uniqueValuesForOption(product, index) {
    var seen = {};
    var values = [];
    product.variants.forEach(function (variant) {
      var v = normalizeOptionValue(variant.options[index]);
      if (v && !seen[v]) {
        seen[v] = true;
        values.push(v);
      }
    });
    return values;
  }

  function isValueAvailable(product, index, value) {
    return product.variants.some(function (variant) {
      return normalizeOptionValue(variant.options[index]) === value && variant.available;
    });
  }

  function findMatchingVariant(product) {
    if (selectedOptions.indexOf(null) !== -1) return null;
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      var matches = true;
      for (var j = 0; j < variant.options.length; j++) {
        if (normalizeOptionValue(variant.options[j]) !== selectedOptions[j]) { matches = false; break; }
      }
      if (matches) return variant;
    }
    return null;
  }

  function updateAddState(product) {
    var variant = findMatchingVariant(product);
    errorEl.hidden = true;
    if (!variant) {
      addBtn.disabled = true;
      addBtn.textContent = 'Select options';
      return;
    }
    priceEl.textContent = formatMoney(variant.price);
    if (!variant.available) {
      addBtn.disabled = true;
      addBtn.textContent = 'Sold out';
      return;
    }
    addBtn.disabled = false;
    addBtn.textContent = '+ Add to cart';
  }

  function handleAdd() {
    var variant = findMatchingVariant(currentProduct);
    if (!variant || !variant.available) return;
    addBtn.disabled = true;
    addBtn.classList.add('is-loading');
    var previousLabel = addBtn.textContent;
    addBtn.textContent = 'Adding\u2026';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variant.id, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error((data && data.description) || 'Unable to add to cart');
          });
        }
        return res.json();
      })
      .then(function () {
        closeModal();
        if (window.theme && window.theme.miniCart) {
          window.theme.miniCart.updateElements();
          window.theme.miniCart.generateCart();
        }
      })
      .catch(function (err) {
        errorEl.hidden = false;
        errorEl.textContent = err.message || 'Unable to add to cart';
        addBtn.disabled = false;
        addBtn.textContent = previousLabel;
      })
      .finally(function () {
        addBtn.classList.remove('is-loading');
      });
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
      return window.theme.Currency.formatMoney(cents, window.theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('.js-upsell-variant-trigger') : null;
    if (!trigger || trigger.classList.contains('disabled')) return;
    e.preventDefault();
    var handle = trigger.getAttribute('data-handle');
    if (handle) openModal(handle);
  });
})();
